import { Injectable } from "@angular/core";
import { Adapter } from "@core/adapter";

/**
 * The maximum budget an organisation allows for a grant type in a given year, scoped to one joint
 * committee (or company-wide when jointCommitteeId is null). There is at most one per
 * (grant, jointCommittee, year); the matrix screen creates one (POST) the first time a cell is filled
 * and edits it (PATCH) afterwards.
 */
export interface GrantYearlyMaximum {
  id: string;
  // The grant type id (last segment of the grant IRI), so it can be keyed against getGrantTypes().
  grantId: string;
  // The joint committee id (last segment of the IRI), or null for a company-wide ceiling.
  jointCommitteeId: string | null;
  jointCommitteeName: string | null;
  year: number;
  maximumAmount: number;
}

/** One editable cell in the maximums matrix: a grant scoped to a committee (null = company-wide). */
export interface GrantMaximumMatrixCell {
  committeeId: string | null;
  committeeName: string | null;
  // The existing ceiling to PATCH/DELETE, or null when nothing is set yet (→ POST).
  maximumId: string | null;
  maximumAmount: number | null;
}

/** One grant row in the maximums matrix. companyWide grants have a single null-committee cell. */
export interface GrantMaximumMatrixRow {
  grantId: string;
  grantName: string;
  companyWide: boolean;
  cells: GrantMaximumMatrixCell[];
}

/** The whole grant × joint-committee grid for a single year. */
export interface GrantMaximumMatrix {
  year: number;
  rows: GrantMaximumMatrixRow[];
}

@Injectable({
  providedIn: 'root'
})
export class GrantYearlyMaximumAdapter implements Adapter<GrantYearlyMaximum> {
  adapt(item: any): GrantYearlyMaximum {
    const grant = item.grant;
    const grantId = typeof grant === 'string'
      ? String(grant).split('/').pop()!
      : (grant?.id ?? (grant?.['@id'] ? String(grant['@id']).split('/').pop() : '') ?? '');
    const jc = item.jointCommittee;
    const jointCommitteeId = jc == null
      ? null
      : (typeof jc === 'string' ? String(jc).split('/').pop()! : (jc?.id ?? (jc?.['@id'] ? String(jc['@id']).split('/').pop() : null) ?? null));
    return {
      id: item.id,
      grantId,
      jointCommitteeId,
      jointCommitteeName: item.jointCommitteeName ?? null,
      year: Number(item.year),
      maximumAmount: item.maximumAmount != null ? Number(item.maximumAmount) : 0,
    };
  }
}
